import {
  createUnifiedTheme,
  palettes,
  genPageTheme,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';
import { toteTokens as tokens } from '@internal/backstage-plugin-deploy-v3';

/**
 * Compact Tote theme for Backstage 1.50.
 *
 * Targets Backstage-internal makeStyles-named components (BackstageHeader,
 * BackstageHeaderTabs, BackstageInfoCard, …). createUnifiedTheme's v5→v4
 * transform copies every `components[name]` entry into v4 `overrides[name]`,
 * so a `BackstageHeaderTabs` key here ends up with the same specificity as
 * the library's own makeStyles rule and wins based on order.
 *
 * MUI-level rules (MuiTab, MuiButton…) tune the material primitives that
 * plugins render directly. Both layers are needed — plugins don't all use
 * Backstage's wrapped components.
 */

function makeCompactTheme(variant: 'light' | 'dark'): UnifiedTheme {
  const palette =
    variant === 'light'
      ? {
          ...palettes.light,
          background: { default: tokens.bg.page, paper: tokens.bg.surface },
          primary: {
            main: tokens.brand.primary,
            dark: tokens.brand.primaryHover,
            light: tokens.brand.primaryLight,
            contrastText: tokens.ink.inverse,
          },
          secondary: { main: tokens.brand.primaryHover },
          textContrast: tokens.ink.base,
          textSubtle: tokens.ink.secondary,
          textVerySubtle: tokens.ink.disabled,
          border: tokens.border.base,
          link: tokens.brand.primary,
          linkHover: tokens.brand.primaryHover,
          navigation: {
            background: tokens.bg.surface,
            indicator: tokens.brand.primary,
            color: tokens.ink.secondary,
            selectedColor: tokens.brand.primary,
            navItem: { hoverBackground: tokens.bg.elevated },
            submenu: { background: tokens.bg.surface },
          },
          pinSidebarButton: {
            icon: tokens.ink.secondary,
            background: tokens.bg.elevated,
          },
          tabbar: { indicator: tokens.brand.primary },
        }
      : palettes.dark;

  // Entity and plugin headers are quiet surfaces. Status colours still come
  // from their explicit banners and badges, never from page identity.
  const lightPage = genPageTheme({
    colors: [tokens.bg.surface],
    shape: 'none',
    options: { fontColor: tokens.ink.base },
  });

  // Pass the base options straight to createUnifiedTheme. It calls
  // createBaseThemeOptions itself, so spreading that function's output in here
  // loses fontFamily and pageTheme: the second call falls back to Backstage's
  // default font and page themes (PLAT-689).
  return createUnifiedTheme({
    palette,
    // Rule 6, engineering-rules.md: Poppins throughout.
    fontFamily: '"Poppins", ui-sans-serif, system-ui, sans-serif',
    defaultPageTheme: 'home',
    pageTheme: {
      home:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      documentation:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave2,
            }),
      tool:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.round,
            }),
      service:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      website:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      library:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      other:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      app:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
      apis:
        variant === 'light'
          ? lightPage
          : genPageTheme({
              colors: ['#1f1f42', '#3b3b6c'],
              shape: shapes.wave,
            }),
    },

    components: {
      // --- Page-level chrome ------------------------------------------------
      // Backstage's banner header (gradient strip w/ title + owner chips)
      BackstageHeader: {
        styleOverrides: {
          header: {
            padding: '16px 24px',
            backgroundImage: 'none',
            backgroundColor:
              variant === 'light' ? tokens.bg.surface : '#383838',
            color: variant === 'light' ? tokens.ink.base : '#F4F4F6',
            borderBottom:
              variant === 'light'
                ? `1px solid ${tokens.border.base}`
                : '1px solid #55555B',
            boxShadow: 'none',
          },
          title: { fontSize: 24, lineHeight: 1.2, fontWeight: 600 },
          subtitle: { fontSize: 13, marginTop: 4 },
          type: { fontSize: 10, marginBottom: 4, letterSpacing: 0.4 },
        },
      } as any,

      // Entity-page tabs — the worst offender in default Backstage (24px pad,
      // uppercase, bold). Give them a sentence-case, 40px-tall row.
      BackstageHeaderTabs: {
        styleOverrides: {
          tabsWrapper: { paddingLeft: 16 },
          defaultTab: {
            padding: '11px 16px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
            minHeight: 44,
            letterSpacing: 0,
            // One line per title: "Private Cluster Package Additional Setups"
            // wrapped at MUI's 264px tab max-width. The tab row scrolls.
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
      } as any,

      // InfoCard — loosened card padding on dense plugin pages
      BackstageInfoCard: {
        styleOverrides: {
          header: { padding: '12px 16px' },
          headerTitle: { fontSize: 15, fontWeight: 600 },
          headerSubheader: { fontSize: 12 },
        },
      } as any,

      // Content shell — catalog/plugin pages wrap their body here.
      // Deliberately NO max-width. A global cap (we tried 1100px on 2026-06-30)
      // looks fine on text/form pages but CRAMPS data-table pages: the catalog is
      // a two-pane layout (filter sidebar + results table), and capping the body
      // left the table ~914px when it needs ~958px → squeezed columns, names
      // wrapping to 3 lines, the ACTIONS column clipped off the right edge. Wide
      // tables, including deploy-v3's status and recent-builds tables, need the
      // full content width. Just comfortable padding.
      BackstageContent: {
        styleOverrides: {
          root: {
            padding: '24px 24px 48px',
          },
        },
      } as any,

      BackstageContentHeader: {
        styleOverrides: {
          container: { marginBottom: 12 },
          // 'About' section title — ContentHeader renders a Typography
          // variant="h4" component="h2". Default h4 (28px) was clipped by
          // plugin-catalog CSS to ~14px. Force something readable.
          title: { fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
        },
      } as any,

      // "View Source" / "View Techdocs" pill row on the entity overview.
      // Library defaults ALL-CAPS + 1.2px letter-spacing which makes two-word
      // labels wrap onto a second line. Normal-case + no extra tracking fixes it.
      BackstageIconLinkVertical: {
        styleOverrides: {
          label: {
            textTransform: 'none',
            fontWeight: 500,
            letterSpacing: 0,
            fontSize: 12,
            whiteSpace: 'nowrap',
          },
        },
      } as any,

      // Sidebar rows — tighter so nav doesn't dominate
      BackstageSidebarItem: {
        styleOverrides: {
          root: { height: 40 },
          label: { fontSize: 13 },
        },
      } as any,

      // --- MUI primitives ---------------------------------------------------
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            paddingTop: 8,
            paddingBottom: 8,
            textTransform: 'none',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            padding: '8px 16px',
            minWidth: 0,
            lineHeight: 1.4,
          },
          sizeSmall: { padding: '5px 12px', fontSize: 13 },
          sizeLarge: { padding: '10px 20px' },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { paddingTop: 10, paddingBottom: 10, fontSize: 14 },
          sizeSmall: { paddingTop: 6, paddingBottom: 6 },
          head: {
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            backgroundColor:
              variant === 'light' ? tokens.bg.elevated : undefined,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: { minHeight: 72 },
        },
      },

      MuiListItem: {
        styleOverrides: {
          root: { paddingTop: 6, paddingBottom: 6 },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: 14 },
          secondary: { fontSize: 12 },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: { padding: 6 },
          sizeSmall: { padding: 4 },
        },
      },

      MuiCardHeader: {
        styleOverrides: {
          root: { paddingTop: 12, paddingBottom: 12 },
          title: { fontSize: 15, fontWeight: 600 },
          subheader: { fontSize: 12 },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 16, '&:last-child': { paddingBottom: 16 } },
        },
      },

      // Form controls — plugin pages use these heavily
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: 14 },
          input: { padding: '12px 14px' },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { fontSize: 13 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: 24, fontSize: 12 },
          sizeSmall: { height: 20, fontSize: 11 },
        },
      },
    },
  });
}

export const toteCompactLight: UnifiedTheme = makeCompactTheme('light');
export const toteCompactDark: UnifiedTheme = makeCompactTheme('dark');
